import { createSelector } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import {
  BookedFlightItineraryWithDepartureTime,
  CallState,
  HotelItinerary,
  MyTripsFilter,
  ItineraryWithType,
  ItineraryEnum,
  Itinerary,
  getDepartureSlice,
  getReturnSlice,
  CarReservation,
  ViewedContactModalProperties,
  FlightItinerarySlice,
  MyTripsProperties,
  UserActionScheduleChangeSuccessProperties,
  FlightProvider,
  Lodging,
  HotelEntryProperties,
  ITrackingProperties,
  ViewedHotelListProperties,
  HotelCfarCancelScenarioEnum,
  HotelCfarRefundable,
  StatementCreditDetail,
  LodgingCollectionEnum,
  CfarEnum,
  HomesItinerary,
  PackageItinerary,
  AncillaryPostBookingMyTripsOffer,
  ExperienceReservation,
} from "redmond";
import { isCaponeTenant } from "@capone/common";

import { TF_PREFIX } from "../../components/ItineraryList/components/ItinerariesModal/components/SelfServeCancelFlightModalContent/constants";
import { IStoreState } from "../../../../reducers/types";
import { config } from "../../../../api/config";
import {
  getIsCorporateForCar,
  getIsCorporateForFlight,
  getIsCorporateForHotel,
} from "../../utils";
import { getAirlineWebLink } from "../../utils";
import {
  getAgentEmail,
  getIsFirstLaunch,
  getRewardsAccounts,
  getSelectedAccount,
  getSelectedAccountIndex,
} from "../../../../modules/rewards/reducer";
import {
  getMyTripsTravelWalletCredit,
  getMyTripsTravelWalletCreditBreakdown,
} from "../../../../modules/travel-wallet/reducer";
import {
  AVAILABLE,
  ActiveExperiments,
  HOTEL_CFAR_REFUND_DISPLAY_80_PERCENT_REFUND_AMOUNT,
  HOTEL_CFAR_REFUND_DISPLAY_NON_REF_PARTIAL,
  HOTEL_CFAR_REFUND_DISPLAY_REFUND_DOLLAR_AMOUNT_ADDITIONAL_PLACES,
} from "../../../../context/experiments";
import { matchSearchQuery } from "../../utils/matchSearchQuery";

const sortByDate = (a?: string, b?: string) => {
  const aDiff = dayjs(a).diff(dayjs());
  const bDiff = dayjs(b).diff(dayjs());
  return aDiff - bDiff;
};

const sortByDescendingDate = (a?: string, b?: string) => {
  const aDiff = dayjs(a).diff(dayjs());
  const bDiff = dayjs(b).diff(dayjs());
  return bDiff - aDiff;
};

export const getAreAllFlightFetchingsNotCalled = (state: IStoreState) =>
  Object.values(state.tripsList.fetchFlightsCallState).every(
    (callState) => callState === CallState.NotCalled,
  );

export const getAreAllFlightFetchingsInTerminalState = (state: IStoreState) =>
  Object.values(state.tripsList.fetchFlightsCallState).every(
    (callState) =>
      callState === CallState.Failed || callState === CallState.Success,
  );

export const getAreSomeFlightFetchingsInProcess = (state: IStoreState) =>
  Object.values(state.tripsList.fetchFlightsCallState).includes(
    CallState.InProcess,
  );

export const getAreTripsLoading = (state: IStoreState) =>
  Object.values(state.tripsList.fetchFlightsCallState).includes(
    CallState.InProcess,
  ) ||
  Object.values(state.tripsList.fetchHotelsCallState).includes(
    CallState.InProcess,
  ) ||
  Object.values(state.tripsList.fetchHomesCallState).includes(
    CallState.InProcess,
  ) ||
  Object.values(state.tripsList.fetchCarsCallState).includes(
    CallState.InProcess,
  ) ||
  Object.values(state.tripsList.fetchPackagesCallState).includes(
    CallState.InProcess,
  ) ||
  Object.values(state.tripsList.fetchExperiencesCallState).includes(
    CallState.InProcess,
  ) ||
  state.tripsList.listWatchCallState === CallState.InProcess;

export const getListWatchesCallState = (state: IStoreState) =>
  state.tripsList.listWatchCallState;

export const getFetchFlightsCallState = (state: IStoreState) =>
  state.tripsList.fetchFlightsCallState;

export const getFetchHotelsCallState = (state: IStoreState) =>
  state.tripsList.fetchHotelsCallState;

export const getFetchHomesCallState = (state: IStoreState) =>
  state.tripsList.fetchHomesCallState;

export const getFetchCarsCallState = (state: IStoreState) =>
  state.tripsList.fetchCarsCallState;

export const getFetchPackagesCallState = (state: IStoreState) =>
  state.tripsList.fetchPackagesCallState;

export const getAcceptScheduleChangeCallState = (state: IStoreState) =>
  state.tripsList.acceptScheduleChangeCallState;

export const getDenyScheduleChangeCallState = (state: IStoreState) =>
  state.tripsList.denyScheduleChangeCallState;

export const getAirlinesMap = createSelector(
  [(state: IStoreState) => state.tripsList.airlines],
  (airlinesMap) => _.cloneDeep(airlinesMap),
);

export const getAirportMap = createSelector(
  [(state: IStoreState) => state.tripsList.airports],
  (airportMap) => _.cloneDeep(airportMap),
);

export const getSelectedFlight = (state: IStoreState) =>
  state.tripsList.selectedFlight;

export const getTripsFilter = (state: IStoreState) =>
  state.tripsList.tripsFilter;

export const getFlights = (state: IStoreState) => state.tripsList.itineraries;

export const getMyTripsPostBookingOffers = (state: IStoreState) =>
  state.tripsList.myTripsPostBookingOffers;

export const getSelectedHotel = (state: IStoreState) =>
  state.tripsList.selectedHotel;

export const getHotels = (state: IStoreState) =>
  state.tripsList.hotelItineraries;

export const getSelectedHome = (state: IStoreState) =>
  state.tripsList.selectedHome;

export const getHomes = (state: IStoreState) => state.tripsList.homeItineraries;

export const getSelectedCar = (state: IStoreState) =>
  state.tripsList.selectedCar;

export const getCars = (state: IStoreState) => state.tripsList.carItineraries;

export const getPackages = (state: IStoreState) =>
  state.tripsList.packageItineraries;

export const getSelectedPackage = (state: IStoreState) =>
  state.tripsList.selectedPackage;

export const getSelectedPackageHotel = (state: IStoreState) =>
  state.tripsList.selectedPackageHotel;

export const getSelectedPackageFlight = (state: IStoreState) =>
  state.tripsList.selectedPackageFlight;

export const getExperiences = (state: IStoreState) =>
  state.tripsList.experienceItineraries;

export const getSelectedExperience = (state: IStoreState) =>
  state.tripsList.selectedExperience;

export const getWatches = (state: IStoreState) => state.tripsList.watches;

export const getWatchContext = (state: IStoreState) =>
  state.tripsList.watchContext;

export const getSelectedFlightCfarInfo = (state: IStoreState) =>
  state.tripsList.selectedCfar?.value;

export const getTripSearchQuery = (state: IStoreState) =>
  state.tripsList.searchQuery;

export const getIsMultiCityCfar = createSelector(
  getSelectedFlightCfarInfo,
  (selectedCfar) =>
    (selectedCfar?.Cfar !== CfarEnum.CancellationPending &&
      selectedCfar?.Cfar !== CfarEnum.ContactCustomerService &&
      selectedCfar?.Cfar !== CfarEnum.TooCloseToDeparture &&
      selectedCfar?.Cfar !== CfarEnum.HtsCfarExercise &&
      selectedCfar?.isMultiCity) ??
    false,
);

export const getSelectedFlightCfarItineraryId = (state: IStoreState) =>
  state.tripsList.selectedCfarItineraryId;

export const getFetchFlightCfarInfoCallState = (state: IStoreState) =>
  state.tripsList.fetchFlightCfarInfoCallState;

export const getFetchFlightCfarInfoCallExpires = (state: IStoreState) =>
  state.tripsList.fetchFlightCfarInfoCallExpires;

export const getFlightCfarCancellationResponse = (state: IStoreState) =>
  state.tripsList.flightCfarCancellationResponse;

export const getConfirmFlightCfarCancellationCallState = (state: IStoreState) =>
  state.tripsList.confirmFlightCfarCancellationCallState;

export const hotelCfarRefundExperimentVariantSelector = (state: IStoreState) =>
  state.tripsList.experiments?.[ActiveExperiments.HOTEL_CFAR_REFUND_DISPLAY];

export const isFintechCsatEnabledSelector = (state: IStoreState) =>
  state.tripsList.experiments?.[ActiveExperiments.FINTECH_CSAT] == AVAILABLE;

export const shouldExcludeCorporateTripsSelector = (state: IStoreState) =>
  isCaponeTenant(config.TENANT) &&
  state.tripsList.experiments?.[
    ActiveExperiments.CORP_MYTRIPS_REPLATFORMING
  ] === AVAILABLE;

// Resolved from the corporate (COBT) LD project in App.tsx; capone-only.
export const isTripsReplatformBannerEnabledSelector = (state: IStoreState) =>
  isCaponeTenant(config.TENANT) &&
  state.tripsList.experiments?.[
    ActiveExperiments.TRIPS_REPLATFORM_BANNER_NOTICE
  ] === AVAILABLE;

export const isHotelCfarRefundDisplayShowAmountAdditionalPlacesEnabledSelector =
  createSelector(
    hotelCfarRefundExperimentVariantSelector,
    (hotelCfarRefundExperimentVariant) =>
      hotelCfarRefundExperimentVariant ===
      HOTEL_CFAR_REFUND_DISPLAY_REFUND_DOLLAR_AMOUNT_ADDITIONAL_PLACES,
  );

export const isHotelCfarRefundDisplay80PercentRefundAmountEnabledSelector =
  createSelector(
    hotelCfarRefundExperimentVariantSelector,
    (hotelCfarRefundExperimentVariant) =>
      hotelCfarRefundExperimentVariant ===
      HOTEL_CFAR_REFUND_DISPLAY_80_PERCENT_REFUND_AMOUNT,
  );

export const isHotelCfarRefundDisplayNonRefPartialEnabledSelector =
  createSelector(
    hotelCfarRefundExperimentVariantSelector,
    (hotelCfarRefundExperimentVariant) =>
      hotelCfarRefundExperimentVariant ===
      HOTEL_CFAR_REFUND_DISPLAY_NON_REF_PARTIAL,
  );

export const getHotelCfarCancellationPolicyCallState = (state: IStoreState) =>
  state.tripsList.hotelCfarCancellationPolicyCallState;

export const getHotelCfarCancellationPolicyCallExpires = (state: IStoreState) =>
  state.tripsList.hotelCfarCancellationPolicyCallExpires;

export const getHotelCfarCancellationPolicyScenario = (state: IStoreState) =>
  state.tripsList.hotelCfarCancellationPolicyScenario;

export const getHotelCfarCancellationStatus = createSelector(
  getHotelCfarCancellationPolicyScenario,
  (hotelCfarCancellationPolicyScenario) =>
    hotelCfarCancellationPolicyScenario?.HotelCfarCancelScenario,
);

export const getHotelCfarCancellationPolicy = createSelector(
  getHotelCfarCancellationPolicyScenario,
  getHotelCfarCancellationStatus,
  (
    hotelCfarCancellationPolicyScenario,
    hotelCfarCancellationStatus,
  ): HotelCfarRefundable | null => {
    if (
      hotelCfarCancellationStatus == HotelCfarCancelScenarioEnum.CfarRefundable
    ) {
      return hotelCfarCancellationPolicyScenario as HotelCfarRefundable;
    }
    return null;
  },
);

export const getSelectedHotelCfarReservationId = (state: IStoreState) =>
  state.tripsList.selectedHotelCfarReservationId;

export const getConfirmHotelCfarCancellationCallState = (state: IStoreState) =>
  state.tripsList.confirmHotelCfarCancellationCallState;

export const getTravelCredits = createSelector(
  [
    getFlights,
    getTripSearchQuery,
    getAirportMap,
    getAirlinesMap,
    shouldExcludeCorporateTripsSelector,
  ],
  (flights, searchQuery, airports, airlines, shouldExcludeCorporateTrips) => {
    if (shouldExcludeCorporateTrips) {
      flights = excludeCorporateItineraries(flights, getIsCorporateForFlight);
    }
    const { past = [], future = [], canceled = [], present = [] } = flights;
    return [...past, ...present, ...future, ...canceled].filter(
      (fl) =>
        matchSearchQuery(searchQuery, airports, airlines)(fl) &&
        !!fl.travelCredit,
    );
  },
);

export const getTripId = createSelector(
  [
    getSelectedFlight,
    getSelectedHotel,
    getSelectedCar,
    getSelectedHome,
    getSelectedPackageFlight,
    getSelectedPackageHotel,
    getSelectedExperience,
  ],
  (
    selectedFlight,
    selectedHotel,
    selectedCar,
    selectedHome,
    selectedPackageFlight,
    selectedPackageHotel,
    selectedExperience,
  ) => {
    if (selectedFlight) {
      return selectedFlight.bookedItinerary.id;
    }
    if (selectedHotel) {
      return selectedHotel.reservation.reservationId;
    }
    if (selectedCar) {
      return selectedCar.bookResult.groundBookingId;
    }
    if (selectedHome) {
      return selectedHome.reservation.id.value;
    }
    if (selectedPackageHotel) {
      return selectedPackageHotel.reservation.reservationBookingId;
    }
    if (selectedPackageFlight) {
      return selectedPackageFlight.bookedItinerary.id;
    }
    if (selectedExperience) {
      return selectedExperience.reservation.customerReservationId;
    }
    return "";
  },
);

const getDepartureDateFromFlight = (
  flight: BookedFlightItineraryWithDepartureTime,
) => getDepartureSlice(flight.bookedItinerary).segments[0]?.scheduledDeparture;

const getReturnDateFromFlight = (
  flight: BookedFlightItineraryWithDepartureTime,
) =>
  getReturnSlice(flight.bookedItinerary)?.segments[0]?.scheduledArrival || "";

const getDateFromHotel = (hotel: HotelItinerary) =>
  hotel.reservation.checkInDate;

const getDateFromHome = (home: HomesItinerary) =>
  home.reservation.stayDates.from;

const getDateFromCar = (car: CarReservation) => car.bookResult.pickUp.dateTime;

const getDropOffDateFromCar = (car: CarReservation) =>
  car.bookResult.dropOff.dateTime;

const getDateFromItinerary = (itinerary: PackageItinerary) =>
  getDepartureSlice(itinerary.flight.itinerary.bookedItinerary)?.segments[0]
    ?.scheduledArrival || "";

const getDateFromExperience = (experience: ExperienceReservation) =>
  experience.reservation.experienceSelection.travelDate;

const getInitialDateForItinerary = (itinerary: ItineraryWithType) => {
  switch (itinerary.type) {
    case ItineraryEnum.Flight:
      return getDepartureDateFromFlight(
        itinerary as BookedFlightItineraryWithDepartureTime,
      );
    case ItineraryEnum.Hotel:
      return getDateFromHotel(itinerary as HotelItinerary);
    case ItineraryEnum.Car:
      return getDateFromCar(itinerary as CarReservation);
    case ItineraryEnum.Home:
      return getDateFromHome(itinerary as HomesItinerary);
    case ItineraryEnum.Package:
      return getDateFromItinerary(itinerary as PackageItinerary);
    case ItineraryEnum.Experience:
      return getDateFromExperience(itinerary as ExperienceReservation);
    default:
      return undefined;
  }
};

const sortItinerariesByDate = (
  a: ItineraryWithType,
  b: ItineraryWithType,
  isPast?: boolean,
) => {
  let aDate = getInitialDateForItinerary(a);
  let bDate = getInitialDateForItinerary(b);

  const getDepDiff = (dateStringX?: string, dateStringY?: string) =>
    isPast
      ? sortByDescendingDate(dateStringX, dateStringY)
      : sortByDate(dateStringX, dateStringY);

  const depDiff = getDepDiff(aDate, bDate);
  if (depDiff === 0) {
    let aReturn;
    let bReturn;

    if (a.type === ItineraryEnum.Flight && b.type === ItineraryEnum.Flight) {
      aReturn = getReturnDateFromFlight(
        a as BookedFlightItineraryWithDepartureTime,
      );
      bReturn = getReturnDateFromFlight(
        b as BookedFlightItineraryWithDepartureTime,
      );
      aDate = aReturn || aDate;
      bDate = bReturn || bDate;
      return getDepDiff(aDate, bDate);
    }
    if (a.type === ItineraryEnum.Car && b.type === ItineraryEnum.Car) {
      aReturn = getDropOffDateFromCar(a as CarReservation);
      bReturn = getDropOffDateFromCar(b as CarReservation);
      aDate = aReturn || aDate;
      bDate = bReturn || bDate;
      return getDepDiff(aDate, bDate);
    }
  }
  return depDiff;
};

const addTypeToItinerary = (
  itineraries: Itinerary[],
  type: ItineraryEnum,
): ItineraryWithType[] =>
  itineraries.map((itinerary) => ({ ...itinerary, type }));

const excludeCorporateItineraries = <T>(
  itineraries: { [key: string]: T[] },
  isCorporate: (itinerary: T) => boolean,
): { [key: string]: T[] } =>
  Object.fromEntries(
    Object.entries(itineraries).map(([key, value]) => [
      key,
      value.filter((itinerary) => !isCorporate(itinerary)),
    ]),
  );

const filterItineraries: (
  tripsFilter: MyTripsFilter,
  hotels: { [key: string]: HotelItinerary[] },
  flights: { [key: string]: BookedFlightItineraryWithDepartureTime[] },
  cars: { [key: string]: CarReservation[] },
  homes: { [key: string]: HomesItinerary[] },
  packages: { [key: string]: PackageItinerary[] },
  experiences: { [key: string]: ExperienceReservation[] },
  offers: AncillaryPostBookingMyTripsOffer[],
  shouldExcludeCorporateTrips?: boolean,
) => {
  hotels: HotelItinerary[];
  flights: BookedFlightItineraryWithDepartureTime[];
  cars: CarReservation[];
  homes: HomesItinerary[];
  packages: PackageItinerary[];
  experiences: ExperienceReservation[];
  isPast: boolean;
} = (
  tripsFilter,
  hotels,
  flights,
  cars,
  homes,
  packages,
  experiences,
  offers,
  shouldExcludeCorporateTrips,
) => {
    if (shouldExcludeCorporateTrips) {
      hotels = excludeCorporateItineraries(hotels, getIsCorporateForHotel);
      flights = excludeCorporateItineraries(flights, getIsCorporateForFlight);
      cars = excludeCorporateItineraries(cars, getIsCorporateForCar);
      packages = excludeCorporateItineraries(packages, (pkg) =>
        getIsCorporateForHotel(pkg.hotel.itinerary),
      );
    }

    const {
      past: pastHotels = [],
      future: futureHotels = [],
      present: presentHotels = [],
      canceled: cancelledHotels = [],
    } = hotels;
    const {
      past: pastFlights = [],
      future: futureFlights = [],
      present: presentFlights = [],
      canceled: canceledFlights = [],
    } = flights;
    const {
      past: pastCars = [],
      future: futureCars = [],
      present: presentCars = [],
      canceled: canceledCars = [],
    } = cars;
    const {
      past: pastHomes = [],
      future: futureHomes = [],
      present: presentHomes = [],
      canceled: canceledHomes = [],
    } = homes;
    const {
      past: pastPackages = [],
      future: futurePackages = [],
      present: presentPackages = [],
      canceled: canceledPackages = [],
    } = packages;
    const {
      past: pastExperiences = [],
      future: futureExperiences = [],
      present: presentExperiences = [],
      canceled: canceledExperiences = [],
    } = experiences;
    const canceledPastFlights = canceledFlights.filter((flight) => {
      const dateDiff = dayjs(
        getReturnDateFromFlight(flight) || getDepartureDateFromFlight(flight),
      ).diff(dayjs());
      return dateDiff <= 0;
    });

    const canceledFutureFlights = canceledFlights.filter((flight) => {
      const dateDiff = dayjs(
        getReturnDateFromFlight(flight) || getDepartureDateFromFlight(flight),
      ).diff(dayjs());
      return dateDiff > 0;
    });

    const canceledPastCars = canceledCars.filter((car) => {
      const dateDiff = dayjs(
        car.bookResult.pickUp.dateTime || car.bookResult.dropOff.dateTime,
      ).diff(dayjs());
      return dateDiff <= 0;
    });

    const canceledFutureCars = canceledCars.filter((car) => {
      const dateDiff = dayjs(
        car.bookResult.pickUp.dateTime || car.bookResult.dropOff.dateTime,
      ).diff(dayjs());
      return dateDiff > 0;
    });

    if (tripsFilter === MyTripsFilter.UPCOMING_TRIPS) {
      return {
        cars: [...futureCars, ...presentCars],
        flights: [...futureFlights, ...presentFlights],
        hotels: [...futureHotels, ...presentHotels],
        homes: [...futureHomes, ...presentHomes],
        packages: [...futurePackages, ...presentPackages],
        experiences: [...futureExperiences, ...presentExperiences],
        isPast: false,
      };
    }
    if (tripsFilter === MyTripsFilter.CANCELED_TRIPS) {
      return {
        cars: [...canceledFutureCars, ...canceledPastCars],
        flights: [...canceledFutureFlights, ...canceledPastFlights],
        hotels: [...cancelledHotels],
        homes: [...canceledHomes],
        packages: [...canceledPackages],
        experiences: [...canceledExperiences],
        isPast: true,
      };
    }
    if (tripsFilter === MyTripsFilter.PAST_TRIPS) {
      return {
        cars: [...pastCars],
        flights: [...pastFlights],
        hotels: [...pastHotels],
        homes: [...pastHomes],
        packages: [...pastPackages],
        experiences: [...pastExperiences],
        isPast: true,
      };
    }
    if (tripsFilter === MyTripsFilter.TRIPS_WITH_OFFERS) {
      return {
        cars: [],
        flights: [...futureFlights, ...presentFlights].filter((flight) =>
          offers?.find(
            (offer) => offer.itineraryId === flight.bookedItinerary.id,
          ),
        ),
        hotels: [],
        homes: [],
        packages: [],
        experiences: [],
        isPast: false,
      };
    }
    return {
      cars: [],
      flights: [],
      hotels: [],
      homes: [],
      packages: [],
      experiences: [],
      isPast: false,
    };
  };

type Itineraries = {
  hotels: HotelItinerary[];
  flights: BookedFlightItineraryWithDepartureTime[];
  cars: CarReservation[];
  homes: HomesItinerary[];
  packages: PackageItinerary[];
  experiences: ExperienceReservation[];
  isPast: boolean;
};

const combineItineraries = (itineraries: Itineraries) =>
  [
    ...addTypeToItinerary(itineraries.cars, ItineraryEnum.Car),
    ...addTypeToItinerary(itineraries.flights, ItineraryEnum.Flight),
    ...addTypeToItinerary(itineraries.hotels, ItineraryEnum.Hotel),
    ...addTypeToItinerary(itineraries.homes, ItineraryEnum.Home),
    ...addTypeToItinerary(itineraries.packages, ItineraryEnum.Package),
    ...addTypeToItinerary(itineraries.experiences, ItineraryEnum.Experience),
  ].sort((a, b) => sortItinerariesByDate(a, b, itineraries.isPast));

export const upcomingItinerariesSelector = createSelector(
  [
    getHotels,
    getFlights,
    getCars,
    getHomes,
    getPackages,
    getExperiences,
    getMyTripsPostBookingOffers,
    shouldExcludeCorporateTripsSelector,
  ],
  (
    hotels,
    flights,
    cars,
    homes,
    packages,
    experiences,
    offers,
    shouldExcludeCorporateTrips,
  ) =>
    filterItineraries(
      MyTripsFilter.UPCOMING_TRIPS,
      hotels,
      flights,
      cars,
      homes,
      packages,
      experiences,
      offers,
      shouldExcludeCorporateTrips,
    ),
);

export const pastItinerariesSelector = createSelector(
  [
    getHotels,
    getFlights,
    getCars,
    getHomes,
    getPackages,
    getExperiences,
    getMyTripsPostBookingOffers,
    shouldExcludeCorporateTripsSelector,
  ],
  (
    hotels,
    flights,
    cars,
    homes,
    packages,
    experiences,
    offers,
    shouldExcludeCorporateTrips,
  ) =>
    filterItineraries(
      MyTripsFilter.PAST_TRIPS,
      hotels,
      flights,
      cars,
      homes,
      packages,
      experiences,
      offers,
      shouldExcludeCorporateTrips,
    ),
);

export const canceledItinerariesSelector = createSelector(
  [
    getHotels,
    getFlights,
    getCars,
    getHomes,
    getPackages,
    getExperiences,
    getMyTripsPostBookingOffers,
    shouldExcludeCorporateTripsSelector,
  ],
  (
    hotels,
    flights,
    cars,
    homes,
    packages,
    experiences,
    offers,
    shouldExcludeCorporateTrips,
  ) =>
    filterItineraries(
      MyTripsFilter.CANCELED_TRIPS,
      hotels,
      flights,
      cars,
      homes,
      packages,
      experiences,
      offers,
      shouldExcludeCorporateTrips,
    ),
);

export const activeCfarCountSelector = createSelector(
  [upcomingItinerariesSelector],
  (upcomingItineraries) =>
    upcomingItineraries.flights.filter((f) => !!f.ancillaries.cfar).length,
);

export const itinerariesToDisplaySelector = createSelector(
  [
    getTripsFilter,
    getHotels,
    getFlights,
    getCars,
    getHomes,
    getPackages,
    getExperiences,
    getTripSearchQuery,
    getAirportMap,
    getAirlinesMap,
    getMyTripsPostBookingOffers,
    shouldExcludeCorporateTripsSelector,
  ],
  (
    tripsFilter,
    hotels,
    flights,
    cars,
    homes,
    packages,
    experiences,
    searchQuery,
    airports,
    airlines,
    offers,
    shouldExcludeCorporateTrips,
  ) =>
    combineItineraries(
      filterItineraries(
        tripsFilter,
        hotels,
        flights,
        cars,
        homes,
        packages,
        experiences,
        offers,
        shouldExcludeCorporateTrips,
      ),
    ).filter(matchSearchQuery(searchQuery, airports, airlines)),
);

export const getTripsCounts = createSelector(
  [
    getHotels,
    getFlights,
    getCars,
    getHomes,
    getPackages,
    getExperiences,
    getWatches,
    getTravelCredits,
    getMyTripsPostBookingOffers,
    getFetchFlightsCallState,
    getFetchCarsCallState,
    shouldExcludeCorporateTripsSelector,
  ],
  (
    hotels,
    flights,
    cars,
    homes,
    packages,
    experiences,
    watches,
    travelCredits,
    offers,
    flightsCallState,
    carsCallState,
    shouldExcludeCorporateTrips,
  ) => {
    if (shouldExcludeCorporateTrips) {
      hotels = excludeCorporateItineraries(hotels, getIsCorporateForHotel);
      flights = excludeCorporateItineraries(flights, getIsCorporateForFlight);
      cars = excludeCorporateItineraries(cars, getIsCorporateForCar);
      packages = excludeCorporateItineraries(packages, (pkg) =>
        getIsCorporateForHotel(pkg.hotel.itinerary),
      );
    }

    const {
      past: pastHotels = [],
      future: futureHotels = [],
      present: presentHotels = [],
      canceled: cancelledHotels = [],
    } = hotels;
    const {
      past: pastFlights = [],
      future: futureFlights = [],
      present: presentFlights = [],
      canceled: canceledFlights = [],
    } = flights;
    const {
      past: pastCars = [],
      future: futureCars = [],
      present: presentCars = [],
      canceled: canceledCars = [],
    } = cars;
    const {
      past: pastHomes = [],
      future: futureHomes = [],
      present: presentHomes = [],
      canceled: canceledHomes = [],
    } = homes;
    const {
      past: pastPackages = [],
      future: futurePackages = [],
      present: presentPackages = [],
      canceled: canceledPackages = [],
    } = packages;
    const {
      past: pastExperiences = [],
      future: futureExperiences = [],
      present: presentExperiences = [],
      canceled: canceledExperiences = [],
    } = experiences;

    const canceledPastFlights = canceledFlights.filter((flight) => {
      const dateDiff = dayjs(
        getReturnDateFromFlight(flight) || getDepartureDateFromFlight(flight),
      ).diff(dayjs());
      return dateDiff <= 0;
    });

    const canceledFutureFlights = canceledFlights.filter((flight) => {
      const dateDiff = dayjs(
        getReturnDateFromFlight(flight) || getDepartureDateFromFlight(flight),
      ).diff(dayjs());
      return dateDiff > 0;
    });
    const canceledPastCars = canceledCars.filter((car) => {
      const dateDiff = dayjs(
        car.bookResult.pickUp.dateTime || car.bookResult.dropOff.dateTime,
      ).diff(dayjs());
      return dateDiff <= 0;
    });

    const canceledFutureCars = canceledCars.filter((car) => {
      const dateDiff = dayjs(
        car.bookResult.pickUp.dateTime || car.bookResult.dropOff.dateTime,
      ).diff(dayjs());
      return dateDiff > 0;
    });

    const hasCanceledLoaded =
      flightsCallState.canceled !== CallState.NotCalled ||
      carsCallState.canceled !== CallState.NotCalled;

    const hasPastLoaded =
      flightsCallState.past !== CallState.NotCalled ||
      carsCallState.past !== CallState.NotCalled;

    const totalUpcomingTrips =
      futureHotels.length +
      futureFlights.length +
      futureCars.length +
      futureHomes.length +
      futurePackages.length +
      futureExperiences.length +
      presentHotels.length +
      presentFlights.length +
      presentCars.length +
      presentHomes.length +
      presentPackages.length +
      presentExperiences.length;

    const totalCanceledTrips =
      canceledFutureFlights.length +
      canceledPastFlights.length +
      cancelledHotels.length +
      canceledFutureCars.length +
      canceledPastCars.length +
      canceledHomes.length +
      canceledPackages.length +
      canceledExperiences.length;

    const totalPastTrips =
      pastHotels.length +
      pastFlights.length +
      pastCars.length +
      pastHomes.length +
      pastPackages.length +
      pastExperiences.length;

    return {
      [MyTripsFilter.UPCOMING_TRIPS]: totalUpcomingTrips,
      [MyTripsFilter.CANCELED_TRIPS]: hasCanceledLoaded
        ? totalCanceledTrips
        : undefined,
      [MyTripsFilter.PAST_TRIPS]: hasPastLoaded
        ? totalPastTrips
        : undefined,
      [MyTripsFilter.TRAVEL_CREDITS]: hasPastLoaded
        ? travelCredits?.length || 0
        : undefined,
      [MyTripsFilter.WATCHED_TRIPS]: watches?.length || 0,
      [MyTripsFilter.TRIPS_WITH_OFFERS]: filterItineraries(
        MyTripsFilter.TRIPS_WITH_OFFERS,
        hotels,
        flights,
        cars,
        homes,
        packages,
        experiences,
        offers,
      ).flights.length,
    };
  },
);

export const watchesSelector = createSelector(
  [
    getTripsFilter,
    getWatches,
    getTripSearchQuery,
    getAirportMap,
    getAirlinesMap,
  ],
  (tripsFilter, watches, searchQuery, airports, airlines) => {
    if (tripsFilter === MyTripsFilter.WATCHED_TRIPS && Array.isArray(watches)) {
      return watches
        .filter(matchSearchQuery(searchQuery, airports, airlines))
        .sort((a, b) =>
          sortByDate(a.key.value.departureDate, b.key.value.departureDate),
        );
    }
    return [];
  },
);

export const getFetchUpcomingFlightsFailed = createSelector(
  getFetchFlightsCallState,
  (flightsCallState) =>
    flightsCallState.present === CallState.Failed ||
    flightsCallState.future === CallState.Failed,
);

export const getFetchPastFlightsFailed = createSelector(
  getFetchFlightsCallState,
  (flightsCallState) =>
    flightsCallState.canceled === CallState.Failed ||
    flightsCallState.past === CallState.Failed,
);

export const getFetchFlightsAllSuccess = createSelector(
  getFetchFlightsCallState,
  (flightsCallState) =>
    flightsCallState.canceled === CallState.Success ||
    flightsCallState.past === CallState.Success ||
    flightsCallState.present === CallState.Success ||
    flightsCallState.future === CallState.Success,
);

export const getFetchFlightsAllFailed = createSelector(
  getFetchFlightsCallState,
  (fetchFlightsCallStates) =>
    fetchFlightsCallStates.canceled === CallState.Failed &&
    fetchFlightsCallStates.past === CallState.Failed &&
    fetchFlightsCallStates.present === CallState.Failed &&
    fetchFlightsCallStates.future === CallState.Failed,
);

export const getFetchUpcomingHotelsFailed = createSelector(
  getFetchHotelsCallState,
  (hotelsCallState) =>
    hotelsCallState.present === CallState.Failed ||
    hotelsCallState.future === CallState.Failed,
);

export const getFetchPastHotelsFailed = createSelector(
  getFetchHotelsCallState,
  (hotelsCallState) =>
    hotelsCallState.canceled === CallState.Failed ||
    hotelsCallState.present === CallState.Failed,
);

export const getFetchUpcomingHomesFailed = createSelector(
  getFetchHomesCallState,
  (homesCallState) =>
    homesCallState.present === CallState.Failed ||
    homesCallState.future === CallState.Failed,
);

export const getFetchPastHomesFailed = createSelector(
  getFetchHomesCallState,
  (homesCallState) =>
    homesCallState.canceled === CallState.Failed ||
    homesCallState.present === CallState.Failed,
);

export const getFetchUpcomingCarsFailed = createSelector(
  getFetchCarsCallState,
  (carsCallState) =>
    carsCallState.present === CallState.Failed ||
    carsCallState.future === CallState.Failed,
);

export const getFetchPastCarsFailed = createSelector(
  getFetchCarsCallState,
  (carsCallState) =>
    carsCallState.canceled === CallState.Failed ||
    carsCallState.present === CallState.Failed,
);

export const getFetchUpcomingPackagesFailed = createSelector(
  getFetchPackagesCallState,
  (packagesCallState) =>
    packagesCallState.present === CallState.Failed ||
    packagesCallState.future === CallState.Failed,
);

export const getFetchPastPackagesFailed = createSelector(
  getFetchPackagesCallState,
  (packagesCallState) =>
    packagesCallState.canceled === CallState.Failed ||
    packagesCallState.present === CallState.Failed,
);

export const getFetchUpcomingTripsFailed = createSelector(
  getFetchUpcomingFlightsFailed,
  getFetchUpcomingHotelsFailed,
  getFetchUpcomingHomesFailed,
  getFetchUpcomingCarsFailed,
  getFetchUpcomingPackagesFailed,
  (
    upcomingFlightsFailed,
    upcomingHotelsFailed,
    upcomingHomesFailed,
    upcomingCarsFailed,
    upcomingPackagesFailed,
  ) =>
    upcomingFlightsFailed ||
    upcomingHotelsFailed ||
    upcomingHomesFailed ||
    upcomingCarsFailed ||
    upcomingPackagesFailed,
);

export const getFetchPastTripsFailed = createSelector(
  getFetchPastFlightsFailed,
  getFetchPastHotelsFailed,
  getFetchPastHomesFailed,
  getFetchPastCarsFailed,
  getFetchPastPackagesFailed,
  (
    pastFlightsFailed,
    pastHotelsFailed,
    pastHomesFailed,
    pastCarsFailed,
    pastPackagesFailed,
  ) =>
    pastFlightsFailed ||
    pastHotelsFailed ||
    pastHomesFailed ||
    pastCarsFailed ||
    pastPackagesFailed,
);

export const hasTripsToDisplay = createSelector(
  [
    getTripsFilter,
    getWatches,
    itinerariesToDisplaySelector,
    upcomingItinerariesSelector,
    pastItinerariesSelector,
    canceledItinerariesSelector,
    getTravelCredits,
    getTripSearchQuery,
    getAirportMap,
    getAirlinesMap,
  ],
  (
    tripsFilter,
    watches,
    itineraries,
    upcomingItineraries,
    pastItineraries,
    canceledItineraries,
    travelCredits,
    searchQuery,
    airports,
    airlines,
  ) => {
    switch (tripsFilter) {
      case MyTripsFilter.UPCOMING_TRIPS:
      case MyTripsFilter.TRIPS_WITH_OFFERS:
        return (
          itineraries.length > 0 &&
          combineItineraries(upcomingItineraries).filter(
            matchSearchQuery(searchQuery, airports, airlines),
          ).length > 0
        );
      case MyTripsFilter.CANCELED_TRIPS:
        return (
          itineraries.length > 0 &&
          combineItineraries(canceledItineraries).filter(
            matchSearchQuery(searchQuery, airports, airlines),
          ).length > 0
        );
      case MyTripsFilter.PAST_TRIPS:
        return (
          itineraries.length > 0 &&
          combineItineraries(pastItineraries).filter(
            matchSearchQuery(searchQuery, airports, airlines),
          ).length > 0
        );
      case MyTripsFilter.WATCHED_TRIPS:
        return watches.length > 0;
      case MyTripsFilter.TRAVEL_CREDITS:
        return travelCredits.length > 0;
      default:
        return false;
    }
  },
);

export const getOpenModal = (state: IStoreState) => state.tripsList.openModal;
export const getRequestPassengerEdit = (state: IStoreState) =>
  state.tripsList.requestPassengerEdit;

export const getDeleteWatchCallState = (state: IStoreState) =>
  state.tripsList.deleteWatchCallState;

export const getUpdateWatchCallState = (state: IStoreState) =>
  state.tripsList.updateWatchCallState;

export const hasFetchTripsError = createSelector(
  [
    getTripsFilter,
    getListWatchesCallState,
    getFetchUpcomingTripsFailed,
    getFetchPastTripsFailed,
  ],
  (tripsFilter, listWatchCallState, upcomingTripFailed, pastTripsFailed) => {
    switch (tripsFilter) {
      case MyTripsFilter.CANCELED_TRIPS:
      case MyTripsFilter.PAST_TRIPS:
        return pastTripsFailed;
      case MyTripsFilter.UPCOMING_TRIPS:
      case MyTripsFilter.TRIPS_WITH_OFFERS:
        return upcomingTripFailed;
      case MyTripsFilter.TRAVEL_CREDITS:
        return upcomingTripFailed && pastTripsFailed;
      case MyTripsFilter.WATCHED_TRIPS:
        return listWatchCallState === CallState.Failed;
      default:
        return false;
    }
  },
);

export const getViewedCarContactModalProperties = (
  car: CarReservation,
  contactReason?: ViewedContactModalProperties["contact_reason"],
): ViewedContactModalProperties => ({
  agent_locator:
    car?.bookResult.confirmationInfo?.supplierBookingId ||
    car?.bookResult.confirmationInfo?.providerBookingId ||
    car?.bookResult.groundBookingId ||
    "",
  agent_locator_provider: "",
  days_until_departure: car
    ? `${dayjs().diff(dayjs(car?.bookResult.pickUp.dateTime), "days")} days`
    : "",
  booking_date: car ? dayjs(car?.bookResult.pickUp.dateTime).toISOString() : "",
  contact_reason: contactReason || "cancel_car",
  contact_provider_ground:
    car?.bookResult.contactSupportInfo?.displayPhone || "",
});

export const getViewedHotelContactModalProperties = (
  hotel: HotelItinerary,
  contactReason?: ViewedContactModalProperties["contact_reason"],
): ViewedContactModalProperties => ({
  agent_locator: hotel?.reservation.reservationId || "",
  agent_locator_provider: "",
  days_until_departure: hotel
    ? `${dayjs().diff(dayjs(hotel?.reservation.checkInDate), "days")} days`
    : "",
  booking_date: hotel
    ? dayjs(hotel?.reservation.checkInDate).toISOString()
    : "",
  contact_reason: contactReason || "cancel_hotel",
  contact_provider_hotel: hotel?.reservation.lodgingData.phone || "",
});

export const getViewedCancelFlightContactModalProperties = (
  flight: BookedFlightItineraryWithDepartureTime,
): ViewedContactModalProperties => {
  const slice: FlightItinerarySlice | null = flight
    ? getDepartureSlice(flight.bookedItinerary)
    : null;
  return {
    agent_locator:
      flight?.bookedItinerary.travelItinerary.locators?.agent.unscopedValue ||
      "",
    agent_locator_provider:
      flight?.bookedItinerary.travelItinerary.locators?.agent.unscopedValue ||
      "",
    days_until_departure: flight
      ? `${dayjs().diff(dayjs(flight.departureTime), "days")} days`
      : "",
    booking_date: flight?.departureTime || "",
    contact_reason: "cancel_flight",
    contact_provider_air:
      slice && slice?.segments.length > 0
        ? slice?.segments[0]?.marketingAirline.code
        : "",
    has_skch: flight?.bookedItinerary.scheduleChange?.id || "false",
    skch_severity: flight?.bookedItinerary.scheduleChange?.severity,
  };
};

export const getViewedFlightExchangeModalProperties = (
  state: IStoreState,
  flight: BookedFlightItineraryWithDepartureTime,
  isPackageFlight?: boolean,
) => {
  const airlineMap = getAirlinesMap(state);
  const redirectUrl = flight ? getAirlineWebLink(flight, airlineMap) : "";
  const {
    bookedItinerary: { scheduleChange, travelItinerary },
    departureTime,
  } = flight;
  const { unscopedValue = "" } = travelItinerary.locators?.agent ?? {};
  const provider = unscopedValue.startsWith(TF_PREFIX)
    ? FlightProvider.travelFusion
    : FlightProvider.sabre;

  return {
    agent_locator: unscopedValue,
    agent_locator_provider: provider,
    booking_date: departureTime,
    days_until_departure: `${dayjs().diff(dayjs(departureTime), "days")} days`,
    contact_provider_air: redirectUrl,
    contact_reason: isPackageFlight ? "change_package_flight" : "change_flight",
    has_skch: scheduleChange?.id || "false",
    skch_severity: scheduleChange?.severity,
  };
};

export const getViewedMajorScheduleChangeProperties = createSelector(
  getSelectedFlight,
  (flight): MyTripsProperties => ({
    agent_locator:
      flight?.bookedItinerary.travelItinerary.locators?.agent.unscopedValue ||
      "",
    agent_locator_provider:
      flight?.bookedItinerary.travelItinerary.locators?.agent.unscopedValue ||
      "",
    days_until_departure: flight
      ? `${dayjs().diff(dayjs(flight.departureTime), "days")} days`
      : "",
    booking_date: flight?.departureTime || "",
    has_skch: flight?.bookedItinerary.scheduleChange?.id || "false",
    skch_severity: flight?.bookedItinerary.scheduleChange?.severity,
  }),
);

export const getUserActionScheduleChangeSuccessProperties = createSelector(
  getSelectedFlight,
  (flight): UserActionScheduleChangeSuccessProperties => ({
    action: "",
    user_action: "",
    severity: flight?.bookedItinerary.scheduleChange?.severity || "",
    system_locator_provider: "",
    agent_locator_provider: "",
    agent_locator:
      flight?.bookedItinerary.travelItinerary.locators?.agent.unscopedValue ||
      "",
    system_locator:
      flight?.bookedItinerary.travelItinerary.locators?.agent.unscopedValue ||
      "",
    days_until_departure: flight
      ? `${dayjs(flight?.departureTime).diff(dayjs(), "day")}`
      : "",
    connection_time_valid: "",
    is_continuous: "",
    has_reprotection: "",
    provider: "",
    pcc: "",
    booking_date: "",
    itinerary_id: "",
    validating_carrier: "",
  }),
);

export const getFetchingFutureFlightSucceded = (state: IStoreState) =>
  state.tripsList.fetchFlightsCallState.future === CallState.Success;

export const getFetchingFutureHotelsSucceded = (state: IStoreState) =>
  state.tripsList.fetchHotelsCallState.future === CallState.Success;

export const getMostUpcomingFlight = (state: IStoreState) => {
  const futureFlights = shouldExcludeCorporateTripsSelector(state)
    ? state.tripsList.itineraries.future.filter(
        (flight) => !getIsCorporateForFlight(flight),
      )
    : state.tripsList.itineraries.future;
  const flightItinerariesWithType = addTypeToItinerary(
    futureFlights,
    ItineraryEnum.Flight,
  );
  const sortedFlights = flightItinerariesWithType.sort((a, b) =>
    sortItinerariesByDate(a, b),
  );
  return sortedFlights[0];
};

export const getPaymentMethods = (state: IStoreState) =>
  state.tripsList.paymentMethods;

export const getHotelAvailabilityTrackingProperties = (state: IStoreState) =>
  state.tripsList.hotelAvailabilityResponse?.trackingPropertiesV2;

export const getCrossSellLatency = (state: IStoreState) =>
  state.tripsList.crossSellLatency;

export const getHotelAvailabilityLodgings = (state: IStoreState) =>
  state.tripsList.hotelAvailabilityResponse?.lodgings;

export const getHotelAvailabilityNextPageToken = (state: IStoreState) =>
  state.tripsList.hotelAvailabilityResponse?.nextPageToken;

export const getHotelBestOfferOverall = (state: IStoreState) =>
  state.tripsList.hotelAvailabilityResponse?.bestOverallOffer;

export const getHotelAvailabilityCallState = (state: IStoreState) =>
  state.tripsList.hotelAvailabilityCallState;

export const getHotelAvailabilitySearchLocationResult = (state: IStoreState) =>
  state.tripsList.hotelSearchLocationResult;

export const getHotelAvailabilityFromDate = (state: IStoreState) =>
  state.tripsList.hotelSearchFromDate;

export const getHotelAvailabilityUntilDate = (state: IStoreState) =>
  state.tripsList.hotelSearchUntilDate;

export const getSearchedNightCount = createSelector(
  getHotelAvailabilityFromDate,
  getHotelAvailabilityUntilDate,
  (fromDate, untilDate) => {
    if (!fromDate || !untilDate) {
      return null;
    }

    return dayjs(untilDate).diff(fromDate, "days");
  },
);

export const getHotelAvailabilityAdultsCount = (state: IStoreState) =>
  state.tripsList.hotelSearchAdultsCount;

export const getHotelAvailabilityChildren = (state: IStoreState) =>
  state.tripsList.hotelSearchChildrenCount;

export const getSelectedLodgingIndex = (state: IStoreState) =>
  state.tripsList.hotelSelectedLodgingIndex;

export const getHotelShopParams = createSelector(
  getHotelAvailabilityFromDate,
  getHotelAvailabilityUntilDate,
  getHotelAvailabilityAdultsCount,
  getHotelAvailabilityChildren,
  getSelectedAccountIndex,
  (fromDate, untilDate, adultsCount, children, selectedAccountIndex) => ({
    fromDate,
    untilDate,
    adultsCount,
    children,
    selectedAccountIndex,
  }),
);

const priceSortComparer = (l1: Lodging, l2: Lodging) => {
  if (!l1.price || !l2.price) {
    if (!l1.price && !l2.price) {
      return 0;
    }
    if (!l1.price) {
      return -1;
    }

    return 1;
  }

  return l1.price.nightlyPrice.fiat.value - l2.price.nightlyPrice.fiat.value;
};

export const orderByPriceLowToHigh = (lodgings: Lodging[]) =>
  lodgings.sort(priceSortComparer);

export const getSortedLodgingsByPricing = createSelector(
  getHotelAvailabilityLodgings,
  (lodgings): Lodging[] => {
    if (lodgings) {
      const { availableLodgings, unavailableLodgings } = lodgings.reduce(
        (prev, curr) => {
          const available =
            typeof curr.available === "undefined" ? true : curr.available;

          if (available) prev.availableLodgings.push(curr);
          else prev.unavailableLodgings.push(curr);

          return prev;
        },
        {
          availableLodgings: [] as Lodging[],
          unavailableLodgings: [] as Lodging[],
        },
      );
      const sortedAvailableHotels = orderByPriceLowToHigh(availableLodgings);
      return sortedAvailableHotels.concat(unavailableLodgings);
    }
    return [];
  },
);

export const getHotelEntryProperties = createSelector(
  getAgentEmail,
  getHotelAvailabilitySearchLocationResult,
  getHotelAvailabilityFromDate,
  getIsFirstLaunch,
  getRewardsAccounts,
  getHotelAvailabilityAdultsCount,
  getHotelAvailabilityChildren,
  getMyTripsTravelWalletCredit,
  getMyTripsTravelWalletCreditBreakdown,
  getHotelAvailabilityLodgings,
  (
    agentEmail,
    location,
    fromDate,
    isFirstLaunch,
    rewardsAccounts,
    numAdults,
    children,
    credit,
    creditBreakdown,
    lodgings,
  ): ITrackingProperties<HotelEntryProperties> => ({
    properties: {
      delegated_to: agentEmail || "",
      first_launch: isFirstLaunch,
      market: location?.label || "",
      country: location?.subLabel || "",
      number_of_properties: lodgings?.length ?? 0,
      number_of_guests: numAdults + children.length,
      adults_count: numAdults,
      children_count: children.length,
      sort_order: "",
      hotel_advance: fromDate
        ? `${dayjs(fromDate).diff(dayjs(), "day")} days`
        : "",
      rooms_searched: 1,
      rewards_accounts: rewardsAccounts
        .map((r) => r.productDisplayName)
        .join(","),
      has_credits: !!credit?.amount?.amount,
      credit_balance: credit?.amount?.amount
        ? Math.abs(credit.amount.amount)
        : 0,
      vx_statement_credit_balance:
        creditBreakdown
          ?.filter((b) => b.CreditDetail === "Statement")
          .reduce(
            (prev, curr) =>
              prev + (curr as StatementCreditDetail).usableAmount.amount,
            0,
          ) || 0,
      ...location?.trackingPropertiesV2?.properties,
    },
    encryptedProperties: [
      location?.trackingPropertiesV2?.encryptedProperties ?? "",
    ],
  }),
);

export const getUnavailableLodgingCount = createSelector(
  getHotelAvailabilityLodgings,
  (lodgings) =>
    lodgings?.filter((lodging) =>
      typeof lodging.available === "undefined" ? false : !lodging.available,
    ).length || 0,
);

export const getAvailableLodgingCount = createSelector(
  getHotelAvailabilityLodgings,
  (lodgings) =>
    lodgings?.filter((lodging) =>
      typeof lodging.available === "undefined" ? false : lodging.available,
    ).length || 0,
);

export const getLuxuryCollectionLodgingCount = createSelector(
  getHotelAvailabilityLodgings,
  (lodgings) =>
    lodgings?.filter(
      (lodging) => lodging.lodgingCollection === LodgingCollectionEnum.Premier,
    ).length,
);

export const getLifestyleCollectionLodgingCount = createSelector(
  getHotelAvailabilityLodgings,
  (lodgings) =>
    lodgings?.filter(
      (lodging) =>
        lodging.lodgingCollection === LodgingCollectionEnum.Lifestyle,
    ).length,
);

export const getViewedHotelListProperties = createSelector(
  getHotelEntryProperties,
  getSelectedAccount,
  getHotelAvailabilityTrackingProperties,
  getUnavailableLodgingCount,
  getAvailableLodgingCount,
  getHotelBestOfferOverall,
  getLuxuryCollectionLodgingCount,
  getLifestyleCollectionLodgingCount,
  (
    properties,
    account,
    trackingProperties,
    unavailableLodgingCount,
    availableLodgingCount,
    bestOverallOffer,
    luxuryLodgingCount,
    lifestyleLodgingCount,
  ): ITrackingProperties<ViewedHotelListProperties> => ({
    properties: {
      ...properties.properties,
      account_type_selected: account?.productDisplayName || "",
      unavailable_hotels: unavailableLodgingCount,
      available_hotels: availableLodgingCount,
      ...bestOverallOffer?.trackingPropertiesV2?.properties,
      has_offer: !!bestOverallOffer,
      account_use_type: account?.accountUseType,
      account_allow_rewards_redemption: account?.allowRewardsRedemption,
      pc_shown: luxuryLodgingCount || 0,
      lc_shown: lifestyleLodgingCount || 0,
      ...trackingProperties?.properties,
      rooms_searched: 1,
    },
    encryptedProperties: [
      bestOverallOffer?.trackingPropertiesV2?.encryptedProperties ?? "",
      trackingProperties?.encryptedProperties ?? "",
    ],
  }),
);

export const getFlightToHotelXSellTripCategory = (state: IStoreState) =>
  state.tripsList.flightToHotelCrossSellTripCategory;

export const getCrossSellOffers = (state: IStoreState) =>
  state.tripsList.crossSellOffers;

export const getFirstCrossellOffer = createSelector(
  getCrossSellOffers,
  (offers) => offers[0],
);

export const getFetchHotelCrossSellCallState = (state: IStoreState) =>
  state.tripsList.fetchHotelCrossSellCallState;

export const getAvailableCrossSellHotels = createSelector(
  getHotelAvailabilityLodgings,
  (lodgings) => lodgings?.filter((lodging) => !!lodging.available),
);

export const getNextUpcomingTrip = createSelector(
  upcomingItinerariesSelector,
  (upcomingItineraries) => combineItineraries(upcomingItineraries)[0] ?? null,
);

export const getActivitiesXsellExperiences = (state: IStoreState) =>
  state.tripsList.activitiesXsellExperiences;

export const getActivitiesXsellCallState = (state: IStoreState) =>
  state.tripsList.activitiesXsellCallState;
